import { RasterWeatherLayer } from '@sofarocean/atlas/types';
import { useQuery } from '@tanstack/react-query';
import { compact } from 'lodash';
import { useMemo } from 'react';

import { SensorType } from '@/api/device';
import {
    ageMaxPng,
    ageMaxSvg,
    ageMinSvg,
    baroDeltaPng,
    baroDeltaSvg,
    baroMaxPng,
    baroMaxSvg,
    baroMinPng,
    baroMinSvg,
    currentMaxPng,
    currentMaxSvg,
    depthMaxPng,
    depthMaxSvg,
    fastestPng,
    fastestSvg,
    oxygenDeltaPng,
    oxygenDeltaSvg,
    oxygenMaxPng,
    oxygenMaxSvg,
    oxygenMinPng,
    oxygenMinSvg,
    salinityDeltaPng,
    salinityDeltaSvg,
    salinityMaxPng,
    salinityMaxSvg,
    salinityMinPng,
    salinityMinSvg,
    splMaxPng,
    splMaxSvg,
    steamiestPng,
    steamiestSvg,
    tempDeltaPng,
    tempDeltaSvg,
    tempMaxPng,
    tempMaxSvg,
    tempMinPng,
    tempMinSvg,
    tiltMaxPng,
    tiltMaxSvg,
    turbidityDeltaPng,
    turbidityDeltaSvg,
    turbidityMaxPng,
    turbidityMaxSvg,
    waterPressureDeltaPng,
    waterPressureDeltaSvg,
    waveMaxPng,
    waveMaxSvg,
    windMaxPng,
    windMaxSvg,
} from '@/assets/topSpotRibbons';
import { RequestOptions, axios } from '@/common/axios';
import { QueryConfig } from '@/common/react-query';
import { APIResponse } from '@/common/types';
import { DatumUnit, QuantityType } from '@/components/data/Datum';

export interface SensorReading {
    sensorPosition: number;
    unitType: string;
    units: string;
    value: number;
}

export interface LeaderboardSample {
    deviceId: number;
    spotterId: string;
    messageId: number;
    timestamp: string;
    sensorReadings: SensorReading[] | null;
    waveHeight: number | null;
    wavePeakPeriod: number | null;
    windSpeed: number | null;
    windDirection: number | null;
    waterTemperature: number | null;
    humidity: number | null;
    internalTemperature: number | null;
    barometricPressure: number | null;
}

export interface LeaderboardEntry {
    memberKey: string;
    spotterId: string;
    value: number;
    unitType: QuantityType;
    units: DatumUnit;
    sample: LeaderboardSample | null;
}

export interface DeltaLeaderboardEntry {
    memberKey: string;
    spotterId: string;
    diff: number;
    unitType: QuantityType;
    units: DatumUnit;
    min: LeaderboardEntry;
    max: LeaderboardEntry;
}

export interface MetricLeaderboard {
    max: LeaderboardEntry[];
    min: LeaderboardEntry[];
    delta: DeltaLeaderboardEntry[];
}

export type LeaderboardData = Record<string, MetricLeaderboard>;

export interface DeviceAgeEntry {
    spotterId: string;
    ageDays: number;
}

export interface LeaderboardStandings {
    oldest_device: DeviceAgeEntry | null;
    youngest_device: DeviceAgeEntry | null;
    most_recently_transmitted: { spotterId: string } | null;
}

type LeaderboardApiResponse = APIResponse<LeaderboardData> & { standings: LeaderboardStandings };

export const getLeaderboard = async (options?: RequestOptions): Promise<LeaderboardData> => {
    const response = await axios.get<LeaderboardApiResponse, LeaderboardApiResponse>('/api/v2/leaderboard', {
        params: { limit: 1 },
        ...options,
    });
    return response.data;
};

type UseLeaderboardOptions = {
    config?: QueryConfig<typeof getLeaderboard>;
};

export const useLeaderboardQuery = ({ config }: UseLeaderboardOptions = {}) =>
    useQuery<LeaderboardData>({
        ...config,
        queryKey: ['leaderboard'],
        queryFn: ({ signal }) => getLeaderboard({ signal }),
        staleTime: 1000 * 15,
    });

export type UseLeaderboardResult = LeaderboardCategoryDefinition &
    (({ stat: 'min' | 'max' } & LeaderboardEntry) | ({ stat: 'delta' } & DeltaLeaderboardEntry));

export const useLeaderboard = (options?: UseLeaderboardOptions): UseLeaderboardResult[] => {
    const { data } = useLeaderboardQuery(options);
    return useMemo(() => {
        if (!data) {
            return [];
        }

        return compact(
            LEADERBOARD_CATEGORIES.map((leaderboardCategory) => {
                const { variable, stat } = leaderboardCategory;
                const leaders = data[variable];
                const leader = leaders?.[stat]?.[0];
                if (!leader) {
                    return null;
                }
                if (stat === 'delta') {
                    const delta = leader as DeltaLeaderboardEntry;
                    if (delta.diff < Math.abs(delta.max.value / 50) && delta.diff < Math.abs(delta.min.value / 50)) {
                        // if the delta is < 2% of the min and max values, it's probably not interesting
                        return null;
                    }
                    delta.spotterId = delta.max.spotterId;
                }
                return { ...leader, ...leaderboardCategory } as UseLeaderboardResult;
            })
        );
    }, [data]);
};

type LeaderboardCategoryDefinition = {
    name: string;
    variable: string;
    stat: keyof MetricLeaderboard;
    weatherLayer?: RasterWeatherLayer;
    subtitle?: string;
    ribbonSvg: string;
    ribbonPng: string;
} & ({ sensorType: SensorType } | { units: DatumUnit; unitType: QuantityType });

const LEADERBOARD_CATEGORIES: LeaderboardCategoryDefinition[] = [
    {
        name: 'Waverly',
        variable: 'wave_height',
        units: 'm',
        unitType: 'distance',
        stat: 'max',
        weatherLayer: 'combinedWaves',
        ribbonSvg: waveMaxSvg,
        ribbonPng: waveMaxPng,
    },
    {
        name: 'Gone With The Wind',
        variable: 'wind_speed',
        units: 'm/s',
        unitType: 'speed',
        stat: 'max',
        weatherLayer: 'wind',
        ribbonSvg: windMaxSvg,
        ribbonPng: windMaxPng,
    },
    {
        name: 'Blaze',
        subtitle: 'Warmest surface water',
        variable: 'water_temperature',
        units: 'C',
        unitType: 'temperature',
        stat: 'max',
        ribbonSvg: tempMaxSvg,
        ribbonPng: tempMaxPng,
    },
    {
        name: 'Ice Ice Baby',
        subtitle: 'Coldest surface water',
        variable: 'water_temperature',
        units: 'C',
        unitType: 'temperature',
        stat: 'min',
        ribbonSvg: tempMinSvg,
        ribbonPng: tempMinPng,
    },
    {
        name: 'Golden Gauge',
        subtitle: 'Widest-ranging surface temperature',
        variable: 'water_temperature',
        units: 'C',
        unitType: 'temperature',
        stat: 'delta',
        ribbonSvg: tempDeltaSvg,
        ribbonPng: tempDeltaPng,
    },
    {
        name: 'Spotter McSteamy',
        variable: 'apparent_temperature',
        units: 'C',
        unitType: 'temperature',
        stat: 'max',
        weatherLayer: 'precipitation',
        ribbonSvg: steamiestSvg,
        ribbonPng: steamiestPng,
    },
    {
        name: 'Bowie',
        variable: 'barometric_pressure',
        units: 'hPa',
        unitType: 'airPressure',
        stat: 'max',
        weatherLayer: 'barometricPressure',
        ribbonSvg: baroMaxSvg,
        ribbonPng: baroMaxPng,
    },
    {
        name: 'Storms A Brewin’',
        variable: 'barometric_pressure',
        weatherLayer: 'barometricPressure',
        units: 'hPa',
        unitType: 'airPressure',
        stat: 'min',
        ribbonSvg: baroMinSvg,
        ribbonPng: baroMinPng,
    },
    {
        name: 'Thor',
        variable: 'barometric_pressure',
        weatherLayer: 'barometricPressure',
        units: 'hPa',
        unitType: 'airPressure',
        stat: 'delta',
        ribbonSvg: baroDeltaSvg,
        ribbonPng: baroDeltaPng,
    },
    {
        name: 'Usain Boat',
        variable: 'drift_speed',
        units: 'm/s',
        unitType: 'speed',
        stat: 'max',
        ribbonSvg: fastestSvg,
        ribbonPng: fastestPng,
    },
    {
        name: 'Sea Biscuit',
        variable: 'sensor:speed',
        sensorType: 'currents',
        weatherLayer: 'currents',
        stat: 'max',
        ribbonSvg: currentMaxSvg,
        ribbonPng: currentMaxPng,
    },
    {
        name: 'Pickle',
        variable: 'sensor:mass_ratio',
        sensorType: 'salinity',
        stat: 'max',
        units: 'PSU',
        unitType: 'salinity',
        ribbonSvg: salinityMaxSvg,
        ribbonPng: salinityMaxPng,
    },
    {
        name: 'Meri Mitchell',
        variable: 'sensor:mass_ratio',
        sensorType: 'salinity',
        stat: 'min',
        units: 'PSU',
        unitType: 'salinity',
        ribbonSvg: salinityMinSvg,
        ribbonPng: salinityMinPng,
    },
    {
        name: 'Widest Salinity Ratio',
        variable: 'sensor:mass_ratio',
        sensorType: 'salinity',
        stat: 'delta',
        units: 'PSU',
        unitType: 'salinity',
        ribbonSvg: salinityDeltaSvg,
        ribbonPng: salinityDeltaPng,
    },
    {
        name: 'Decibel Diva',
        variable: 'sensor:sound_pressure_level',
        subtitle: 'Loudest hydrophone reading',
        sensorType: 'hydrophone',
        stat: 'max',
        ribbonSvg: splMaxSvg,
        ribbonPng: splMaxPng,
    },
    {
        name: 'Rollin’ in the Deep',
        variable: 'sensor:pressure',
        sensorType: 'pressure',
        stat: 'max',
        ribbonSvg: depthMaxSvg,
        ribbonPng: depthMaxPng,
    },
    {
        name: 'Bobber',
        variable: 'sensor:pressure',
        sensorType: 'pressure',
        stat: 'delta',
        ribbonSvg: waterPressureDeltaSvg,
        ribbonPng: waterPressureDeltaPng,
    },
    {
        name: 'Tilt-a-whirl',
        subtitle: 'Most tilted current meter',
        variable: 'sensor:tilt',
        sensorType: 'currents',
        stat: 'max',
        ribbonSvg: tiltMaxSvg,
        ribbonPng: tiltMaxPng,
    },
    {
        name: 'Pea Soup',
        variable: 'sensor:turbidity',
        sensorType: 'turbidity',
        stat: 'max',
        ribbonSvg: turbidityMaxSvg,
        ribbonPng: turbidityMaxPng,
    },
    {
        name: 'Widest Turbidity Range',
        variable: 'sensor:turbidity',
        sensorType: 'turbidity',
        stat: 'delta',
        ribbonSvg: turbidityDeltaSvg,
        ribbonPng: turbidityDeltaPng,
    },
    {
        name: 'Hot Blooded',
        subtitle: 'Warmest subsurface temperature',
        variable: 'sensor:temperature',
        sensorType: 'waterTemperature',
        stat: 'max',
        ribbonSvg: tempMaxSvg,
        ribbonPng: tempMaxPng,
    },
    {
        name: 'Cold As Ice',
        subtitle: 'Coldest subsurface temperature',
        variable: 'sensor:temperature',
        sensorType: 'waterTemperature',
        stat: 'min',
        ribbonSvg: tempMinSvg,
        ribbonPng: tempMinPng,
    },
    {
        name: 'Big Dipper',
        subtitle: 'Biggest subsurface temperature change',
        variable: 'sensor:temperature',
        sensorType: 'waterTemperature',
        stat: 'delta',
        ribbonSvg: tempDeltaSvg,
        ribbonPng: tempDeltaPng,
    },
    {
        name: 'Bubbles',
        variable: 'sensor:mass_concentration',
        sensorType: 'dissolvedOxygen',
        stat: 'max',
        ribbonSvg: oxygenMaxSvg,
        ribbonPng: oxygenMaxPng,
    },
    {
        name: 'Anna Robic',
        variable: 'sensor:mass_concentration',
        sensorType: 'dissolvedOxygen',
        stat: 'min',
        ribbonSvg: oxygenMinSvg,
        ribbonPng: oxygenMinPng,
    },
    {
        name: 'Widest Oxygen Range',
        variable: 'sensor:mass_concentration',
        sensorType: 'dissolvedOxygen',
        stat: 'delta',
        ribbonSvg: oxygenDeltaSvg,
        ribbonPng: oxygenDeltaPng,
    },
    {
        name: 'Sea Dog',
        subtitle: 'Oldest spotter',
        variable: 'age',
        stat: 'max',
        units: 'd',
        unitType: 'time',
        ribbonSvg: ageMaxSvg,
        ribbonPng: ageMaxPng,
    },
    {
        name: 'Splashton Kutcher',
        subtitle: 'Youngest spotter',
        variable: 'age',
        stat: 'min',
        units: 'd',
        unitType: 'time',
        ribbonSvg: ageMinSvg,
        ribbonPng: ageMaxPng,
    },
];
